import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {getIconColorClassName, getIconComponent} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {CopyIcon, KebabHorizontalIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Heading, IconButton, useConfirm} from '@primer/react'

import type {SavedView} from '../../components/views/types'
import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useDeleteSavedViewMutation} from '../../hooks/use-delete-saved-view-mutation'
import {useDuplicateSavedViewDialog} from '../../hooks/use-duplicate-saved-view-dialog'
import styles from './SavedViewHeader.module.css'

function ViewHeaderIcon({icon: Icon, color}: {icon: ReturnType<typeof getIconComponent>; color: string}) {
  return <Icon size={16} className={`${styles.titleIcon} ${getIconColorClassName(color)}`} />
}

interface SavedViewHeaderProps {
  savedView: SavedView
  owner: string
  repo: string
  viewsPath: string
  onStartEditing: () => void
}

export function SavedViewHeader({savedView, owner, repo, viewsPath, onStartEditing}: SavedViewHeaderProps) {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const {mutate: deleteView, isPending: isDeleting} = useDeleteSavedViewMutation({owner, repo})
  const {openDuplicateDialog, duplicateDialog} = useDuplicateSavedViewDialog({owner, repo})

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: LABELS.views.deleteTitle,
      content: LABELS.views.deleteContent(savedView.name),
      confirmButtonContent: LABELS.views.deleteConfirmationButton,
      confirmButtonType: 'danger',
    })
    if (!confirmed) return
    deleteView(
      {id: savedView.id},
      {
        onSuccess: () => navigate(viewsPath),
      },
    )
  }

  return (
    <div className={styles.titleWrapper}>
      <div className={styles.header}>
        <div className={styles.titleArea}>
          <ViewHeaderIcon icon={getIconComponent(savedView.icon)} color={savedView.color} />
          <Heading as="h1" className={styles.title}>
            {savedView.name}
          </Heading>
        </div>
        <div className={styles.actionsGrp}>
          <CreateIssueButton
            label={BUTTON_LABELS.newIssue}
            navigate={navigate}
            optionConfig={{
              issueCreateArguments: {
                repository: {owner, name: repo},
              },
              showRepositoryPicker: false,
              showFullScreenButton: true,
              canBypassTemplateSelection: true,
              navigate,
            }}
          />
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label={`${savedView.name} options`} disabled={isDeleting} />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="small">
              <ActionList>
                <ActionList.Item onSelect={onStartEditing} disabled={isDeleting}>
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  {LABELS.views.edit}
                </ActionList.Item>
                <ActionList.Item onSelect={() => openDuplicateDialog(savedView)} disabled={isDeleting}>
                  <ActionList.LeadingVisual>
                    <CopyIcon />
                  </ActionList.LeadingVisual>
                  {LABELS.views.duplicate}
                </ActionList.Item>
                <ActionList.Item onSelect={handleDelete} disabled={isDeleting} variant="danger">
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  {LABELS.views.delete}
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      </div>
      {savedView.description && <p className={styles.description}>{savedView.description}</p>}
      {duplicateDialog}
    </div>
  )
}
